/**
 * Formie form utilities
 * Handles CSRF refresh, cancel buttons, and error display for Formie forms
 */

interface FormieInitEvent extends CustomEvent {
    detail: {
        formie: {
            refreshForCache: (formId: string) => void;
        };
        formId: string;
    };
}

interface FormieErrorEvent extends CustomEvent {
    detail: {
        errors?: {
            form?: string[];
        };
    };
}

/**
 * Position cancel button next to submit button in modal forms
 */
function positionCancelButton(formId: string): void {
    const form = document.getElementById(formId);
    const submitWrapper = form?.querySelector('.submitWrapper');

    if (form && submitWrapper) {
        // Check if this form already has a cancel button
        let cancelBtn = submitWrapper.querySelector('.formie-cancel-btn');

        if (!cancelBtn) {
            // Create a new cancel button for this specific form
            cancelBtn = document.createElement('button');
            cancelBtn.setAttribute('type', 'button');
            cancelBtn.className = 'formie-cancel-btn text-lg font-semibold font-primary text-gray-700 underline underline-offset-4 hover:no-underline cursor-pointer transition-all duration-200';
            cancelBtn.setAttribute('aria-label', 'Cancel and close form');
            cancelBtn.innerHTML = '<span>Cancel</span>';

            // Add click event listener to close the modal
            cancelBtn.addEventListener('click', function() {
                // Find the close button within the same modal and click it
                const modal = form.closest('[role="dialog"]');
                if (modal) {
                    const closeBtn = modal.querySelector<HTMLElement>('[x-ref="closeBtn"]');
                    if (closeBtn) {
                        closeBtn.click();
                        return;
                    }
                }

                // Fallback: try to find Alpine component and call toggle function
                const alpineComponent = form.closest<HTMLElement>('[x-data]');
                if (alpineComponent && window.Alpine) {
                    const alpineData = window.Alpine.$data(alpineComponent) as Record<string, unknown>;
                    if (typeof alpineData?.toggleFormSlideIn === 'function') {
                        (alpineData.toggleFormSlideIn as () => void)();
                    } else if (typeof alpineData?.toggleFormFadeIn === 'function') {
                        (alpineData.toggleFormFadeIn as () => void)();
                    }
                }
            });
        }

        // Add the cancel button to the submitWrapper container
        submitWrapper.appendChild(cancelBtn);
    }
}

/**
 * Handle Formie form submission errors
 */
function handleFormieError(event: FormieErrorEvent): void {
    const errorHtml = document.querySelector('.fui-alert-top-form');

    if (event.detail.errors?.form && errorHtml) {
        const errorList = document.createElement('ul');
        event.detail.errors.form.forEach(err => {
            const li = document.createElement('li');
            li.textContent = err;
            errorList.appendChild(li);
        });
        errorHtml.appendChild(errorList);
    }
}

/**
 * Initialize Formie form handlers
 * @param options Configuration options
 */
export function initFormieHandlers(options: { withCancelButton?: boolean } = {}): void {
    const { withCancelButton = false } = options;

    // Handle form initialization
    document.addEventListener('onFormieInit', ((event: FormieInitEvent) => {
        const Formie = event.detail.formie;
        const formId = event.detail.formId;

        // Refresh the necessary bits that are statically cached (CSRF inputs, captchas, etc)
        Formie.refreshForCache(formId);

        // Position cancel button for modal forms
        if (withCancelButton) {
            positionCancelButton(formId);
        }
    }) as EventListener);

    // Handle form submission errors
    document.addEventListener('onFormieSubmitError', ((event: FormieErrorEvent) => {
        handleFormieError(event);
    }) as EventListener);
}

// Auto-initialize for all forms (CSRF refresh and error handling)
// Cancel button is opt-in via data attribute
document.addEventListener('DOMContentLoaded', () => {
    // Set up global error handler
    document.addEventListener('onFormieSubmitError', ((event: FormieErrorEvent) => {
        handleFormieError(event);
    }) as EventListener);

    // Set up CSRF refresh on form init
    document.addEventListener('onFormieInit', ((event: FormieInitEvent) => {
        const Formie = event.detail.formie;
        const formId = event.detail.formId;

        // Refresh CSRF tokens
        Formie.refreshForCache(formId);

        // Check if this form needs a cancel button (modal forms)
        const form = document.getElementById(formId);
        if (form?.closest('[role="dialog"]')) {
            positionCancelButton(formId);
        }
    }) as EventListener);
});
